import { useCallback, useEffect, useState } from 'react';
import { BigButton } from '../../../../components/BigButton';
import { GenericTextInput } from '../../../../components/Inputs/styles';
import ToggleInput from '../../../../components/Inputs/ToggleInput';
import formaters from './logic';
import { Dropdown } from '../../../../components/Dropdown';
import useFormModalAddressLogic from '../../logic/useFormModalAddressLogic';
import { AddressFormContainer, Row, SpanRequired } from './styles';
import { states } from './logic';
import useCountryCodes from '../../../../hooks/useCountryCodes';
import { BasicLoading } from '../../../../components/BasicLoading';
import shopService from '../../../../services/salesShopItems.service';
import { useAddressModalStore } from '../../../../store/address/addressModalStore';

interface Props {
    closeModal: () => void;
}

export function AddressForm({ closeModal }: Props) {
    const {
        errors,
        register,
        handleSuccess,
        handleError,
        handleSubmit,
        getValues,
        setValue,
        watch,
        validatingFields,
        trigger,
    } = useFormModalAddressLogic();

    const addressModal = useAddressModalStore();

    const values = watch();
    const countryCodes = useCountryCodes();

    const [isForeigner, setIsForeigner] = useState<boolean>(
        getValues('foreigner')
    );

    const handleToggle = useCallback(() => {
        setIsForeigner(!isForeigner);
        setValue('foreigner', !isForeigner);
        addressModal.update({
            address: { ...addressModal.address, foreigner: !isForeigner },
        });
        setValue('address', '');
        setValue('number', '');
        setValue('neighborhood', '');
        setValue('city', '');
        setValue('state', '');
        setValue('complement', '');
        setValue('zipCode', '');
    }, [isForeigner, setValue]);

    const dropdownCountriesContent = countryCodes.data
        ?.map(item => ({
            id: item.iso2,
            value: item.name,
        }))
        .sort((a, b) => a.value.localeCompare(b.value, 'pt-br'));

    const handleAutocomplete = async () => {
        const response = await shopService.fetchAddressDataByCep(
            getValues('zipCode')
        );
        const data = response;

        if (!getValues('address').length) setValue('address', data.logradouro);
        if (!getValues('city').length) setValue('city', data.localidade);
        if (!getValues('neighborhood').length)
            setValue('neighborhood', data.bairro);
        if (!getValues('state').length) setValue('state', data.uf);
    };

    useEffect(() => {
        if (getValues('zipCode').length === 9 && !getValues('foreigner')) {
            handleAutocomplete();
        }
    }, [getValues, setValue, validatingFields.zipCode]);

    // if (!countryCodes.data) return null;

    if (!getValues('foreigner'))
        return (
            <AddressFormContainer
                onSubmit={e => {
                    e.stopPropagation();
                    handleSubmit(handleSuccess, handleError)(e);
                }}
            >
                <BasicLoading isLoading={countryCodes.isLoading} />
                <div className="foreigner-div">
                    <ToggleInput
                        onChange={handleToggle}
                        defaultValue={isForeigner}
                    />
                    <p>Foreigner</p>
                </div>
                <Row>
                    <div className="input-item">
                        <label htmlFor="cep">
                            CEP
                            <SpanRequired>*</SpanRequired>
                        </label>
                        <GenericTextInput
                            id="cep"
                            type="text"
                            placeholder="00000-000"
                            maxLength={9}
                            {...register('zipCode', {
                                onChange: formaters.formatCEPInput,
                            })}
                            aria-invalid={Boolean(errors['zipCode'])}
                        />
                        {errors['zipCode'] && (
                            <span className="error-span">
                                {errors['zipCode'].message}
                            </span>
                        )}
                    </div>
                    <div className="input-item input-allWidth">
                        <label htmlFor="address">
                            Endereço
                            <SpanRequired>*</SpanRequired>
                        </label>
                        <GenericTextInput
                            id="address"
                            type="text"
                            placeholder="Digite seu endereço"
                            maxLength={100}
                            {...register('address')}
                            aria-invalid={Boolean(errors['address'])}
                        />
                        {errors['address'] && (
                            <span className="error-span">
                                {errors['address'].message}
                            </span>
                        )}
                    </div>
                    <div className="input-item">
                        <label htmlFor="number">
                            Número
                            <SpanRequired>*</SpanRequired>
                        </label>
                        <GenericTextInput
                            id="number"
                            type="text"
                            placeholder="123"
                            maxLength={10}
                            {...register('number', {
                                onChange: formaters.formatNumberInput,
                            })}
                            aria-invalid={Boolean(errors['number'])}
                        />
                        {errors['number'] && (
                            <span className="error-span">
                                {errors['number'].message}
                            </span>
                        )}
                    </div>
                </Row>
                <Row>
                    <div className="input-item input-allWidth">
                        <label htmlFor="neighborhood">
                            Bairro
                            <SpanRequired>*</SpanRequired>
                        </label>
                        <GenericTextInput
                            id="neighborhood"
                            type="text"
                            placeholder="Digite seu bairro"
                            maxLength={100}
                            {...register('neighborhood')}
                            aria-invalid={Boolean(errors['neighborhood'])}
                        />
                        {errors['neighborhood'] && (
                            <span className="error-span">
                                {errors['neighborhood'].message}
                            </span>
                        )}
                    </div>
                    <div className="input-item input-allWidth">
                        <label htmlFor="city">
                            Cidade
                            <SpanRequired>*</SpanRequired>
                        </label>
                        <GenericTextInput
                            id="city"
                            type="text"
                            placeholder="Digite sua cidade"
                            maxLength={100}
                            {...register('city')}
                            aria-invalid={Boolean(errors['city'])}
                        />
                        {errors['city'] && (
                            <span className="error-span">
                                {errors['city'].message}
                            </span>
                        )}
                    </div>
                    <div
                        className="input-item input-dropdown"
                        onClick={e => e.preventDefault()}
                    >
                        <label htmlFor="state">
                            Estado
                            <SpanRequired>*</SpanRequired>
                        </label>
                        <Dropdown
                            title=""
                            elements={states.map(state => {
                                return {
                                    id: state,
                                    value: state,
                                };
                            })}
                            maxWidth={500}
                            allSize={true}
                            currentSelected={values.state || 'AC'}
                            setCurrentSelected={d => {
                                setValue('state', d as string);
                                trigger();
                            }}
                        />
                        {errors['city'] && (
                            <span className="error-span">
                                {errors['city'].message}
                            </span>
                        )}
                    </div>
                </Row>
                <Row>
                    <div
                        className="input-item input-dropdown "
                        onClick={e => e.preventDefault()}
                    >
                        <label htmlFor="country">
                            País
                            <SpanRequired>*</SpanRequired>
                        </label>
                        {countryCodes.data && (
                            <Dropdown
                                title=""
                                elements={dropdownCountriesContent || []}
                                maxWidth={500}
                                allSize={true}
                                currentSelected={(() => {
                                    const targetIso = values.countryIso2;

                                    return countryCodes.data.find(
                                        e => e.iso2 === targetIso
                                    )?.name;
                                })()}
                                setCurrentSelected={d => {
                                    const iso = countryCodes.data!.find(
                                        e => e.name === d
                                    )!;
                                    iso;
                                    setValue('countryIso2', iso.iso2);
                                    trigger();
                                }}
                            />
                        )}
                        {errors['countryIso2'] && (
                            <span className="error-span">
                                {errors['countryIso2'].message}
                            </span>
                        )}
                    </div>
                    <div className="input-item input-allWidth">
                        <label htmlFor="complement">Complemento</label>
                        <GenericTextInput
                            id="complement"
                            type="text"
                            placeholder="Apartamento 12, bloco F..."
                            maxLength={100}
                            {...register('complement')}
                            aria-invalid={Boolean(errors['complement'])}
                        />
                    </div>
                </Row>
                <Row>
                    <BigButton
                        bgColor={'secondary'}
                        color={'primary'}
                        allWidth={true}
                        type="button"
                        onClick={closeModal}
                    >
                        Cancelar
                    </BigButton>
                    <BigButton
                        bgColor={'primary'}
                        color={'primary'}
                        allWidth={true}
                        type="submit"
                    >
                        Adicionar Local
                    </BigButton>
                </Row>
            </AddressFormContainer>
        );

    return (
        <AddressFormContainer
            onSubmit={e => {
                e.stopPropagation();
                e.preventDefault();
                handleSubmit(handleSuccess, handleError)(e);
            }}
        >
            <BasicLoading isLoading={countryCodes.isLoading} />
            <div className="foreigner-div">
                <ToggleInput
                    onChange={handleToggle}
                    defaultValue={isForeigner}
                />
                <p>Foreigner</p>
            </div>
            <Row>
                <div
                    className="input-item input-dropdown "
                    onClick={e => e.preventDefault()}
                >
                    <label htmlFor="country">
                        Country
                        <SpanRequired>*</SpanRequired>
                    </label>
                    {countryCodes.data && (
                        <Dropdown
                            title=""
                            elements={dropdownCountriesContent || []}
                            maxWidth={500}
                            allSize={true}
                            currentSelected={(() => {
                                const targetIso = values.countryIso2;

                                return countryCodes.data.find(
                                    e => e.iso2 === targetIso
                                )?.name;
                            })()}
                            setCurrentSelected={d => {
                                const iso = countryCodes.data!.find(
                                    e => e.name === d
                                )!;
                                iso;
                                setValue('countryIso2', iso.iso2);
                                trigger();
                            }}
                            injectedTippyProps={{ appendTo: document.body }}
                        />
                    )}
                    {errors['countryIso2'] && (
                        <span className="error-span">
                            {errors['countryIso2'].message}
                        </span>
                    )}
                </div>
                <div className="input-item">
                    <label htmlFor="zipCode">
                        ZIP CODE
                        <SpanRequired>*</SpanRequired>
                    </label>
                    <GenericTextInput
                        id="zipCode"
                        type="text"
                        placeholder="00000"
                        maxLength={10}
                        {...register('zipCode')}
                        aria-invalid={Boolean(errors['zipCode'])}
                    />
                    {errors['zipCode'] && (
                        <span className="error-span">
                            {errors['zipCode'].message}
                        </span>
                    )}
                </div>
            </Row>
            <Row>
                <BigButton
                    bgColor={'secondary'}
                    color={'primary'}
                    allWidth={true}
                    type="button"
                    onClick={closeModal}
                >
                    Cancel
                </BigButton>
                <BigButton
                    bgColor={'primary'}
                    color={'primary'}
                    allWidth={true}
                    type="submit"
                >
                    Add Location
                </BigButton>
            </Row>
        </AddressFormContainer>
    );
}
